import { useState, useMemo } from 'react';
import {
  AddIcon,
  Alert,
  Button,
  PageHeader,
  Spinner,
  Tooltip,
} from '@billda/design-system';
import { usePlatformOrganisations } from '../hooks/usePlatformOrganisations';
import { PageToolbar } from '../../../shared/components/layout/PageToolbar';
import { ChipTabs } from '../../../shared/components/ui/ChipTabs';
import { PlatformOrganisationsListView } from '../components/organisms/PlatformOrganisationsListView';
import { CreateOrganisationDialog } from '../components/organisms/CreateOrganisationDialog';

type OrgTypeFilter = 'customers' | 'all' | 'e2e';

const ORG_TYPE_TABS = [
  { label: 'Customers', value: 'customers' },
  { label: 'All', value: 'all' },
  { label: 'E2E', value: 'e2e' },
] as const;

const isE2EOrg = (name: string) => name.startsWith('E2E');

const PlatformOrganisationsPage = () => {
  const [searchValue, setSearchValue] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<OrgTypeFilter>('customers');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const {
    data: organisations,
    isLoading,
    error,
  } = usePlatformOrganisations(appliedSearch || undefined);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setAppliedSearch(value);
  };

  const filteredOrganisations = useMemo(() => {
    const orgs = organisations ?? [];
    if (typeFilter === 'all') return orgs;
    if (typeFilter === 'e2e') return orgs.filter(o => isE2EOrg(o.name));
    return orgs.filter(o => !isE2EOrg(o.name)); // customers
  }, [organisations, typeFilter]);

  if (isLoading && !organisations) {
    return (
      <div className="flex min-h-[200px] items-center justify-center p-6">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <Alert severity="error" className="mb-4">
        {error.message}
      </Alert>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="min-h-0 flex-1 overflow-auto">
        <div>
          <PageHeader
            title="Organisations"
            actions={
              <Tooltip content="Create Organisation">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setCreateDialogOpen(true)}
                  aria-label="Create Organisation"
                  data-testid="create-organisation-button"
                >
                  <AddIcon aria-hidden="true" className="size-5" />
                </Button>
              </Tooltip>
            }
          />

          <PageToolbar
            tabs={
              <ChipTabs
                tabs={[...ORG_TYPE_TABS]}
                value={typeFilter}
                onChange={v => setTypeFilter(v as OrgTypeFilter)}
                data-testid="org-type-tabs"
              />
            }
            searchValue={searchValue}
            onSearchChange={handleSearchChange}
          />

          <PlatformOrganisationsListView
            organisations={filteredOrganisations}
            loading={isLoading}
          />
        </div>
      </div>
      <CreateOrganisationDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />
    </div>
  );
};

export default PlatformOrganisationsPage;
